import { where, documentId, Timestamp, arrayUnion, arrayRemove, serverTimestamp, deleteField, runTransaction, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchTeamById, setInjured } from '../teams/api';
import {
  fetchByTable,
  addDataToTable,
  updateDataById,
  deleteDocById,
  getDateFromTimestamp,
} from '../utils';

export const fetchShifts = async setLoading =>
  fetchByTable({ tableName: 'shifts', orderByField: 'startDate', setLoading });

export const fetchShiftsByUser = async (userId, setLoading) =>
  fetchByTable({
    tableName: 'shifts',
    whereCondition: where('userId', '==', userId),
    orderByField: 'startDate',
    setLoading,
  });

export const fetchShiftById = async (id, setLoading) =>
  fetchByTable({
    tableName: 'shifts',
    whereCondition: where(documentId(), '==', id),
    setLoading,
  });

export const fetchShiftsByPlaceId = async (id, setLoading) =>
  fetchByTable({
    tableName: 'shifts',
    whereCondition: where('placeId', '==', id),
    setLoading,
  });

export const addShift = async (shift, setLoading) =>
  addDataToTable({ tableName: 'shifts', data: shift, setLoading });

// Universal guard against per-team overshoot. Whenever a caller writes a
// `players` array we run the write inside a Firestore transaction that
// re-reads the shift for its `totalPlayers`, counts local/visit on the
// incoming array, and refuses the commit if either side would exceed
// `totalPlayers / 2`. This is the last line of defense: every path that
// mutates the roster — join button, invite accept, addPlayersModal,
// promote-sub, formation drag, cross-team swap, and any future caller —
// gets the same protection here even if it forgets to check on its own.
// Writes that don't touch `players` (isClosed, price, etc.) fall through
// to the plain updateDataById fast-path with no extra read.
export const updateShift = async (id, data, setLoading) => {
  if (!Array.isArray(data?.players)) {
    return updateDataById({ id, tableName: 'shifts', data, setLoading });
  }
  const shiftRef = doc(db, 'shifts', id);
  if (setLoading) setLoading(true);
  try {
    return await runTransaction(db, async transaction => {
      const snap = await transaction.get(shiftRef);
      if (!snap.exists()) throw new Error('Shift not found');
      const perTeamLimit = Math.floor((snap.data()?.totalPlayers ?? 0) / 2);
      const localCount = data.players.filter(p => p?.isLocal).length;
      const visitCount = data.players.filter(p => !p?.isLocal).length;
      if (perTeamLimit > 0 && (localCount > perTeamLimit || visitCount > perTeamLimit)) {
        // Error técnico con `code` para que la UI lo traduzca. Este guard
        // no debería dispararse en flujos normales (el cascade a sub/lista
        // evita el overshoot); si sale es un bug que hay que investigar.
        const err = new Error(
          `Per-team cap overshoot blocked: ${localCount}L + ${visitCount}V would exceed ${perTeamLimit} per side`,
        );
        err.code = 'shifts.error.perTeamCap';
        throw err;
      }
      transaction.update(shiftRef, data);
      return undefined;
    });
  } finally {
    if (setLoading) setLoading(false);
  }
};

// eslint-disable-next-line no-unused-vars
export const addFriend = async (userId, _friends, playerMail, setLoading) =>
  updateDataById({
    id: userId,
    tableName: 'users',
    // arrayUnion is atomic — no race condition when multiple clients write concurrently.
    // The _friends param is kept for backward compatibility but no longer used.
    data: { friendsIds: arrayUnion(playerMail) },
    setLoading,
  });

export const removeShift = async (id, setLoading) =>
  deleteDocById({ tableName: 'shifts', id, setLoading });

export const closeShift = async (id, close, setLoading) =>
  updateDataById({
    id,
    tableName: 'shifts',
    data: { isClosed: close },
    setLoading,
  });

export const removeStatsByShift = async (id, setLoading) => {
  if (setLoading) setLoading(true);
  try {
    const values = await fetchByTable({
      tableName: 'stats',
      whereCondition: where('shiftId', '==', id),
    });
    await Promise.all(values.map(value => deleteDocById({ tableName: 'stats', id: value.id })));
  } catch (e) {
    console.log(e);
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const addStat = async (stat, setLoading) =>
  addDataToTable({ tableName: 'stats', data: stat, setLoading });

export const addLeagueResult = async result =>
  addDataToTable({ tableName: 'leagueResults', data: result });

export const fetchLeagueResultByShift = async shiftId =>
  fetchByTable({ tableName: 'leagueResults', whereCondition: where('shiftId', '==', shiftId) });

export const updateLeagueResultPenalty = async (shiftId, penaltyWinner, localPenaltyGoals, visitPenaltyGoals) => {
  const values = await fetchByTable({
    tableName: 'leagueResults',
    whereCondition: where('shiftId', '==', shiftId),
  });
  await Promise.all([
    // Update leagueResults collection
    ...values.map(v => updateDataById({
      id: v.id,
      tableName: 'leagueResults',
      data: { penaltyWinner, localPenaltyGoals, visitPenaltyGoals },
    })),
    // Also mirror penalty result onto the shift document so shift cards can display it
    updateDataById({
      id: shiftId,
      tableName: 'shifts',
      data: { penaltyWinner, localPenaltyGoals, visitPenaltyGoals },
    }),
  ]);
};

export const removeLeagueResultsByShift = async shiftId => {
  const values = await fetchByTable({
    tableName: 'leagueResults',
    whereCondition: where('shiftId', '==', shiftId),
  });
  await Promise.all(values.map(v => deleteDocById({ tableName: 'leagueResults', id: v.id })));
};

/**
 * Fully deletes a shift and all its associated data:
 * stats entries, leagueResults (if a league match), and the shift document itself.
 */
export const syncProfileToShifts = async (uid, { nickname, photoURL }) => {
  const upcomingShifts = await fetchByTable({
    tableName: 'shifts',
    whereCondition: where('startDate', '>=', Timestamp.now()),
  });

  const shiftsToUpdate = upcomingShifts.filter(shift =>
    (shift.players ?? []).some(p => p.userId === uid) ||
    (shift.playRequests ?? []).some(p => p.userId === uid),
  );

  await Promise.all(
    shiftsToUpdate.map(shift => {
      const updatedPlayers = (shift.players ?? []).map(p =>
        p.userId === uid ? { ...p, nickname, photoUrl: photoURL } : p,
      );
      const updatedRequests = (shift.playRequests ?? []).map(p =>
        p.userId === uid ? { ...p, nickname, photoUrl: photoURL } : p,
      );
      return updateDataById({
        id: shift.id,
        tableName: 'shifts',
        data: { players: updatedPlayers, playRequests: updatedRequests },
      });
    }),
  );
};

export const deleteShiftFull = async (shiftId, leagueId, setLoading) => {
  if (setLoading) setLoading(true);
  try {
    await removeStatsByShift(shiftId);
    if (leagueId) {
      await removeLeagueResultsByShift(shiftId);
    }
    await deleteDocById({ tableName: 'shifts', id: shiftId });
    // If this was the last match of a league, reset isStarted so the
    // "Iniciar liga" button reappears without needing a new league.
    if (leagueId) {
      const remaining = await fetchByTable({
        tableName: 'shifts',
        whereCondition: where('leagueId', '==', leagueId),
      });
      if (remaining.length === 0) {
        await updateDataById({
          id: leagueId,
          tableName: 'leagues',
          data: { isStarted: false },
        });
      }
    }
  } catch (e) {
    console.error(e);
  } finally {
    if (setLoading) setLoading(false);
  }
};

// Saves post-match data (rating, MVP, field condition, comments) to the shift document.
// Fire-and-forget — failures are caught by the caller.
// Deprecated: kept for legacy shift docs still rendering old postMatch fields
// (rating/mvp/comments). New shifts use savePostMatchResponse below.
export const savePostMatch = async (shiftId, data) =>
  updateDataById({ id: shiftId, tableName: 'shifts', data: { postMatch: data } });

// Writes a single participant's survey response into
// `shift.postMatch.responses.{uid}` via a dotted-path update so it never
// overwrites peers' answers. `skipped: true` marks a "Enviar sin responder"
// choice — still counts as answered (won't re-prompt) but stays out of the
// aggregate averages.
export const savePostMatchResponse = async (shiftId, userId, response) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { [`postMatch.responses.${userId}`]: { ...response, submittedAt: Timestamp.now() } },
  });

export const joinWaitlist = async (shiftId, entry) =>
  updateDataById({ id: shiftId, tableName: 'shifts', data: { waitlist: arrayUnion(entry) } });

// Ledger-style payment entries stored on the shift doc.
//   fieldPayments      → cash the organizer/co-organizer paid to the venue
//   coOrgToOrgPayments → cash the co-organizer handed to the creator as
//                         settlement of the collected-cash surplus
// Each entry is { id, amount, at, byUserId, byName } — id lets us
// arrayRemove a specific row without a filter+set race.
const buildPaymentEntry = (amount, user) => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  amount: Number(amount) || 0,
  at: Timestamp.now(),
  byUserId: user?.uid ?? '',
  byName: user?.nickname ?? '',
});

export const addFieldPayment = async (shiftId, amount, user) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { fieldPayments: arrayUnion(buildPaymentEntry(amount, user)) },
  });

export const removeFieldPayment = async (shiftId, entry) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { fieldPayments: arrayRemove(entry) },
  });

export const addCoOrgToOrgPayment = async (shiftId, amount, user) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { coOrgToOrgPayments: arrayUnion(buildPaymentEntry(amount, user)) },
  });

export const removeCoOrgToOrgPayment = async (shiftId, entry) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { coOrgToOrgPayments: arrayRemove(entry) },
  });

// Chat-style notes between the shift creator and its co-organizer. Same
// entry shape as the payment ledger so the renderer can reuse formatting.
export const addOrgNote = async (shiftId, text, user) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: {
      orgNotes: arrayUnion({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        text: (text || '').trim(),
        at: Timestamp.now(),
        byUserId: user?.uid ?? '',
        byName: user?.nickname ?? '',
      }),
    },
  });

export const removeOrgNote = async (shiftId, entry) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { orgNotes: arrayRemove(entry) },
  });

// Marks the match as started and patches each player/sub with the puntual
// switch value coming from the StartMatchModal. `latesByUserId` is a Set of
// userIds that were flipped to "tarde"; everyone else defaults to puntual.
// Stores `startedAt` on the shift so downstream code can differentiate matches
// whose puntualidad was captured from legacy ones (no field = default puntual
// for backward compat).
export const startShift = async (shiftId, currentShift, latesByUserId, setLoading) => {
  const markPunctual = list =>
    (list ?? []).map(p => ({ ...p, punctual: !latesByUserId.has(p.userId) }));
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: {
      startedAt: serverTimestamp(),
      players: markPunctual(currentShift.players),
      subs: markPunctual(currentShift.subs),
    },
    setLoading,
  });
};

// Toggles a single player's puntual field. Detects whether the target is in
// `players` or `subs` and updates only that array.
export const setPlayerPunctual = async (shiftId, currentShift, userId, punctual, setLoading) => {
  const inPlayers = (currentShift.players ?? []).some(p => p.userId === userId);
  const key = inPlayers ? 'players' : 'subs';
  const patched = (currentShift[key] ?? []).map(p =>
    (p.userId === userId ? { ...p, punctual } : p),
  );
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { [key]: patched },
    setLoading,
  });
};

// Adds a user to the shift's private-match whitelist. Called both when the
// organizer shares in-app to a friend, and when an invitee opens the shift
// link — so private matches stay visible in their list without needing to join.
export const addInvitedUserToShift = async (shiftId, userId) => {
  if (!shiftId || !userId) return null;
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { invitedUserIds: arrayUnion(userId) },
  });
};

// Cancels a match invitation for one user, cleaning up both surfaces:
//   1. Removes the uid from shift.invitedUserIds (so private matches no
//      longer appear in the invitee's list).
//   2. Deletes any pending `requests` docs the client is allowed to remove
//      for that shift + invitee (rules limit delete to from/to/admin, so
//      the notif goes away for the caller when they created the invite and
//      for the invitee when they open their own inbox — best effort here).
// The invitee's roster spot, response, or team-side membership are NOT
// touched — that's a separate action, not an "unarchive invitation".
export const removeInvitedUserFromShift = async (shiftId, userId) => {
  if (!shiftId || !userId) return null;
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { invitedUserIds: arrayRemove(userId) },
  });
};

export const removeShiftInvitationRequests = async (shiftId, invitedUserId, invitedEmail) => {
  if (!shiftId) return;
  try {
    const requests = await fetchByTable({
      tableName: 'requests',
      whereCondition: where('shiftId', '==', shiftId),
    });
    const targets = requests.filter(r => (
      (invitedUserId && (r.toUserId === invitedUserId || r.userId === invitedUserId))
      || (invitedEmail && r.to === invitedEmail)
    ));
    await Promise.all(targets.map(r => deleteDocById({ tableName: 'requests', id: r.id }).catch(() => {})));
  } catch (e) {
    // Best-effort cleanup — rules may reject some deletes; UI still succeeds.
  }
};

// Stamps the shift with a team id (management-side link, populated by the
// team's bulk-invite flow). Used to query "shifts of this team" for the
// per-shift attendance/status view.
export const addTeamToShift = async (shiftId, teamId) => {
  if (!shiftId || !teamId) return null;
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { teamIds: arrayUnion(teamId) },
  });
};

// Persists a player's response to a match invite: 'confirmed' | 'declined'
// with an optional reason & note. Stored under `responses[userId]` on the
// shift so it lives independently of the `players[]` roster (accepting still
// requires the normal join flow — this only captures intent).
export const setShiftResponse = async (shiftId, userId, response) => {
  if (!shiftId || !userId) return null;
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { [`responses.${userId}`]: { ...response, at: Date.now() } },
  });
};

// Removes the user's response entirely — they go back to "no respondió".
export const clearShiftResponse = async (shiftId, userId) => {
  if (!shiftId || !userId) return null;
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { [`responses.${userId}`]: deleteField() },
  });
};

// Chooses the side for a self-join in a shift. Preference: explicit
// invitedTeamMap hint → team with fewer players → local by default.
export const pickJoinSide = (shift, userId) => {
  const mapped = shift?.invitedTeamMap?.[userId];
  if (mapped === 'local' || mapped === 'visit') return mapped;
  const local = (shift?.players ?? []).filter(p => p.isLocal).length;
  const visit = (shift?.players ?? []).filter(p => !p.isLocal).length;
  return local <= visit ? 'local' : 'visit';
};

// Runs `mutator(freshShift)` inside a Firestore transaction. The mutator
// receives the CURRENT persisted shift (not any stale in-memory copy) and
// returns the update payload to write. Any handler that writes to
// `players` / `subs` / `waitlist` should go through this — the transaction
// guarantees no other write slipped in between the cap check and the
// commit, so two concurrent operations cannot both push past a per-team
// cap. As a last-ditch backstop, the resulting `players` array is
// counted per side and the write is aborted if either side would exceed
// `totalPlayers / 2` — protecting even future callers that forget to
// re-check.
//
// Return `null` from the mutator to skip the write entirely (idempotent
// no-op). Throw to abort with a domain-specific error.
export const runShiftPlayersUpdate = async (shiftId, mutator) => {
  if (!shiftId) throw new Error('shiftId is required');
  const shiftRef = doc(db, 'shifts', shiftId);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(shiftRef);
    if (!snap.exists()) throw new Error('Shift not found');
    const fresh = { id: shiftId, ...snap.data() };
    const update = await mutator(fresh, transaction);
    if (!update) return null;
    if (Array.isArray(update.players)) {
      const perTeamLimit = Math.floor((fresh.totalPlayers ?? 0) / 2);
      const localCount = update.players.filter(p => p.isLocal).length;
      const visitCount = update.players.filter(p => !p.isLocal).length;
      if (perTeamLimit > 0 && (localCount > perTeamLimit || visitCount > perTeamLimit)) {
        const err = new Error(
          `Per-team cap overshoot blocked: ${localCount}L + ${visitCount}V would exceed ${perTeamLimit} per side`,
        );
        err.code = 'shifts.error.perTeamCap';
        throw err;
      }
    }
    transaction.update(shiftRef, update);
    return update;
  });
};

// Capacity snapshot for a shift. All "is the match full?" checks must be
// per-team — treating players.length >= totalPlayers as "full" misreports a
// skewed roster (e.g. 9v7 in an 8v8) because both sides sum to totalPlayers
// even when one side still has open spots.
export const getShiftCapacity = shift => {
  const spotsPerTeam = Math.floor((shift?.totalPlayers ?? 0) / 2);
  const localCount = (shift?.players ?? []).filter(p => p.isLocal).length;
  const visitCount = (shift?.players ?? []).filter(p => !p.isLocal).length;
  const freeLocal = Math.max(0, spotsPerTeam - localCount);
  const freeVisit = Math.max(0, spotsPerTeam - visitCount);
  return {
    spotsPerTeam,
    localCount,
    visitCount,
    freeLocal,
    freeVisit,
    freeSpots: freeLocal + freeVisit,
    isFull: freeLocal + freeVisit === 0,
    isTeamFull: isLocal => (isLocal ? freeLocal : freeVisit) === 0,
  };
};

// Self-join: adds `user` to the shift's players[] on the given (or auto-
// picked) side, or to subs / waitlist if that team is full. Idempotent —
// no-op when the user already appears anywhere on players/subs/waitlist.
// Returns the outcome ('joined' | 'sub' | 'waitlist' | 'alreadyIn').
//
// Runs inside a runTransaction so two concurrent joins to the same side
// cannot both pass the per-team cap check and end up with 9 in an 8-slot
// team. Also appends an event log to shift.logs atomically, so every
// join path (button, invite accept, response row) leaves a chat trace —
// the previous arrayUnion write skipped the log for invite acceptances.
//
// `messages` provides the pre-formatted log strings so the caller keeps
// control of i18n:
//   { joinedLocal?, joinedVisit?, joinedSub?, waitlistJoin? }
// The right joined* is picked based on the resolved side. Missing keys
// silently skip the log for that outcome.
export const joinShiftAsPlayer = async (shift, user, sideHint, messages = null) => {
  if (!shift?.id || !user?.uid) return null;
  const shiftRef = doc(db, 'shifts', shift.id);
  const subSlots = shift.substitutes ?? 0;

  return runTransaction(db, async transaction => {
    const snap = await transaction.get(shiftRef);
    if (!snap.exists()) return null;
    const fresh = { id: shift.id, ...snap.data() };

    const alreadyIn = [
      ...(fresh.players ?? []),
      ...(fresh.subs ?? []),
      ...(fresh.waitlist ?? []),
    ].some(p => p.userId === user.uid);
    if (alreadyIn) return 'alreadyIn';

    const side = sideHint === 'local' || sideHint === 'visit'
      ? sideHint
      : pickJoinSide(fresh, user.uid);
    const isLocal = side === 'local';
    const perTeamLimit = (fresh.totalPlayers ?? 0) / 2;
    const teamPlayers = (fresh.players ?? []).filter(p => p.isLocal === isLocal).length;
    const teamSubs = (fresh.subs ?? []).filter(s => s.isLocal === isLocal).length;

    const entry = {
      userId: user.uid,
      nickname: user.nickname ?? '',
      photoUrl: user.photoURL ?? '',
      email: user.email ?? '',
      age: user.age ?? 0,
      level: user.level ?? '',
      position: user.position ?? '',
      isLocal,
      goals: 0,
      absent: false,
      yellowCard: 0,
      redCard: false,
    };

    const appendLog = (data, logDef) => {
      if (!logDef) return data;
      const logEntry = {
        date: Timestamp.fromDate(new Date()),
        type: 'event',
        ...(typeof logDef === 'string'
          ? { message: logDef }
          : { messageId: logDef.messageId, messageValues: logDef.messageValues }),
      };
      return { ...data, logs: [...(fresh.logs ?? []), logEntry] };
    };

    if (teamPlayers < perTeamLimit) {
      const joinedMsg = isLocal ? messages?.joinedLocal : messages?.joinedVisit;
      transaction.update(shiftRef, appendLog(
        { players: [...(fresh.players ?? []), entry] },
        joinedMsg,
      ));
      return 'joined';
    }
    if (teamSubs < subSlots) {
      transaction.update(shiftRef, appendLog(
        { subs: [...(fresh.subs ?? []), { ...entry, isSub: true }] },
        messages?.joinedSub,
      ));
      return 'sub';
    }
    const waitlistEntry = {
      userId: user.uid,
      nickname: user.nickname ?? '',
      photoUrl: user.photoURL ?? '',
      email: user.email ?? '',
      position: user.position ?? '',
      level: user.level ?? '',
      age: user.age ?? 0,
      messageToken: user.messageToken ?? null,
    };
    transaction.update(shiftRef, appendLog(
      { waitlist: [...(fresh.waitlist ?? []), waitlistEntry] },
      messages?.waitlistJoin,
    ));
    return 'waitlist';
  });
};

// Builds the i18n-agnostic message set joinShiftAsPlayer expects. Callers
// pass this once and joinShiftAsPlayer picks the right outcome message
// based on which side/list the user actually landed on after the
// transaction — so we don't have to know the resolved side up front.
export const buildJoinMessages = ({ nickname, localTeamName, visitTeamName }) => ({
  joinedLocal: { messageId: 'shifts.event.joined', messageValues: { name: nickname, team: localTeamName ?? '' } },
  joinedVisit: { messageId: 'shifts.event.joined', messageValues: { name: nickname, team: visitTeamName ?? '' } },
  joinedSub: { messageId: 'shifts.event.joinedSub', messageValues: { name: nickname } },
  waitlistJoin: { messageId: 'shifts.event.waitlistJoin', messageValues: { name: nickname } },
});

// Self-leave: pulls the user out of players / subs / waitlist if present.
// Uses filtered writes (arrayRemove needs an exact-match object which we
// don't have for stored entries with timestamps). No-op when the user
// wasn't in any list.
export const leaveShiftAsPlayer = async (shift, user) => {
  if (!shift?.id || !user?.uid) return null;
  const { uid } = user;
  const inPlayers = (shift.players ?? []).some(p => p.userId === uid);
  const inSubs = (shift.subs ?? []).some(p => p.userId === uid);
  const inWaitlist = (shift.waitlist ?? []).some(p => p.userId === uid);
  if (!inPlayers && !inSubs && !inWaitlist) return null;
  return updateDataById({
    id: shift.id,
    tableName: 'shifts',
    data: {
      ...(inPlayers && { players: (shift.players ?? []).filter(p => p.userId !== uid) }),
      ...(inSubs && { subs: (shift.subs ?? []).filter(s => s.userId !== uid) }),
      ...(inWaitlist && { waitlist: (shift.waitlist ?? []).filter(w => w.userId !== uid) }),
    },
  });
};

// Single entry point for the 3-way invite response (Confirmar / Lesionado
// / No puedo) from anywhere that only has a shiftId (e.g. the notification
// inbox). Fetches the shift, mirrors what ShiftResponseRow does when the
// component already has the shift in memory: mutates the roster, writes
// the response, and — for injured — flips team.injuredUserIds on every
// team the shift is stamped with WHERE THE USER IS ALREADY A MEMBER.
// Never adds anyone to a team.
// `formatMessage` is optional — pass react-intl's formatter and this
// resolves the chat-log messages from the shift's team names post-fetch.
// Callers that don't have it (server-side scripts) can skip it and the
// join will simply not append a chat log entry.
export const respondToInvite = async (shiftId, user, choice, sideHint, formatMessage = null) => {
  if (!shiftId || !user?.uid) return null;
  const rows = await fetchShiftById(shiftId);
  const shift = rows?.[0];
  if (!shift) throw new Error('Shift not found');
  const { uid } = user;
  const target = {
    uid,
    nickname: user.nickname ?? '',
    photoURL: user.photoURL ?? '',
    email: user.email ?? '',
    position: user.position ?? '',
    age: user.age ?? 0,
    level: user.level ?? '',
  };
  const flipInjured = async injured => {
    const teamIds = shift.teamIds ?? [];
    await Promise.all(teamIds.map(async tid => {
      const team = await fetchTeamById(tid).catch(() => null);
      if (!team) return;
      const isMember = (team.memberUserIds ?? []).includes(uid)
        || (team.members ?? []).some(m => m.userId === uid);
      if (!isMember) return;
      await setInjured({ id: tid }, uid, injured).catch(() => {});
    }));
  };
  let outcome = null;
  if (choice === 'confirmed') {
    const messages = buildJoinMessages({
      nickname: target.nickname,
      localTeamName: shift.localTeam?.name,
      visitTeamName: shift.visitTeam?.name,
    });
    outcome = await joinShiftAsPlayer(shift, target, sideHint, messages); // 'joined' | 'sub' | 'waitlist' | 'alreadyIn'
    await setShiftResponse(shiftId, uid, { status: 'confirmed', reason: '', note: '' });
    await flipInjured(false);
  } else if (choice === 'injured') {
    await leaveShiftAsPlayer(shift, target);
    await setShiftResponse(shiftId, uid, { status: 'declined', reason: 'injury', note: '' });
    await flipInjured(true);
  } else if (choice === 'unavailable') {
    await leaveShiftAsPlayer(shift, target);
    await setShiftResponse(shiftId, uid, { status: 'declined', reason: '', note: '' });
    await flipInjured(false);
  }
  return outcome;
};

// Stamps which team an invitee was invited to. Only meaningful in friendly
// mode: `getViewerSide` reads this map when a user has been invited but not
// yet joined a roster, so they still see the right side. `side` is 'local'
// or 'visit'; passing anything else is a no-op.
export const stampInvitedTeam = async (shiftId, userId, side) => {
  if (!shiftId || !userId) return null;
  if (side !== 'local' && side !== 'visit') return null;
  return updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { [`invitedTeamMap.${userId}`]: side },
  });
};

export const leaveWaitlist = async (shiftId, currentWaitlist, userId) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: { waitlist: currentWaitlist.filter(e => e.userId !== userId) },
  });

// Computes the result of moving a player to the OTHER team, cascading through
// the target team's available slots: full roster → substitute → waitlist.
// Pure function — returns the new players/subs/waitlist arrays plus which
// outcome happened, so the caller can persist it (and, for FormationTab,
// combine it with a formation-slot assignment in the same flow).
// Traduce un error de las APIs de shifts a un mensaje user-facing usando
// el `code` que le pusimos al Error. Cae en 'common.error' si no matchea.
// Uso: catch (e) { message.error(translateShiftError(e, formatMessage)); }
export const translateShiftError = (err, formatMessage) => {
  const code = err?.code;
  if (code && typeof formatMessage === 'function') {
    try {
      const msg = formatMessage({ id: code });
      if (msg && msg !== code) return msg;
    } catch { /* fallthrough */ }
  }
  if (typeof formatMessage === 'function') {
    return formatMessage({ id: 'common.error' });
  }
  return err?.message || 'Error';
};

export const computeTeamMove = (shift, player, subSlots) => {
  const targetIsLocal = !player.isLocal;
  // Math.floor para alinear con el cap del guard en updateShift /
  // runShiftPlayersUpdate. Sin floor, en partidos con totalPlayers
  // impar (ej. 11 -> 5.5) el cascade permitía llegar a 6 por lado y
  // el guard rechazaba la escritura con "cap overshoot blocked".
  const perTeamLimit = Math.floor((shift.totalPlayers ?? 0) / 2);
  const targetPlayerCount = shift.players.filter(p => p.isLocal === targetIsLocal).length;
  const targetSubCount = (shift.subs ?? []).filter(s => s.isLocal === targetIsLocal).length;

  const remainingPlayers = shift.players.filter(p => p.userId !== player.userId);
  const remainingSubs = (shift.subs ?? []).filter(s => s.userId !== player.userId);

  if (targetPlayerCount < perTeamLimit) {
    return {
      outcome: 'player',
      targetIsLocal,
      players: [...remainingPlayers, { ...player, isLocal: targetIsLocal }],
      subs: remainingSubs,
      waitlist: shift.waitlist ?? [],
    };
  }
  if (targetSubCount < subSlots) {
    return {
      outcome: 'sub',
      targetIsLocal,
      players: remainingPlayers,
      subs: [...remainingSubs, { ...player, isLocal: targetIsLocal }],
      waitlist: shift.waitlist ?? [],
    };
  }
  // Waitlist entry preserves the full player shape so a later re-accept
  // doesn't have to reconstruct fields or fall back to a profile fetch.
  // isLocal is dropped intentionally — it's only meaningful once the entry
  // is placed back on a roster, and the accept flow sets it there.
  const { isLocal, ...playerWithoutSide } = player;
  return {
    outcome: 'waitlist',
    targetIsLocal,
    players: remainingPlayers,
    subs: remainingSubs,
    waitlist: [...(shift.waitlist ?? []), playerWithoutSide],
  };
};

// All userIds involved in a shift (players + subs + organizer), deduped.
// Used to fan out match-wide notifications to every participant.
export const getShiftParticipantIds = shift => {
  const ids = new Set();
  (shift.players ?? []).forEach(p => p.userId && ids.add(p.userId));
  (shift.subs ?? []).forEach(p => p.userId && ids.add(p.userId));
  if (shift.userId) ids.add(shift.userId);
  return [...ids];
};

// Queue a match-wide notification. The server fans it out: for each recipient it
// looks up token + prefs, pushes if the `type` is enabled, and (when `archive`)
// writes to their notification history. Participants carry only `userId`, so the
// server resolves the FCM token from users/{userId}.
export const notifyMatchParticipants = async ({
  shiftId,
  senderUserId,
  recipientUserIds,
  type,
  title,
  body = '',
  url = '',
  from = '',
  startDate = '',
  archive = true,
  // When true, the sender (organizer) also receives the push — used for
  // match-level changes (date/place/cancellation) they want mirrored to their
  // other devices. Defaults false so player join/leave/kick events don't
  // self-spam the actor. History is always written regardless.
  pushSender = false,
  // Clarity fields (all optional). `action` is a nameless, third-person verb
  // phrase ("cambió la fecha") the UI pairs with the actor's name; `actorPhoto`
  // is the actor's avatar; `matchDate`/`placeName` identify the match. When
  // `action` is present the server builds the push as "{from} {action}" /
  // "{placeName} · {matchDate}"; otherwise it falls back to title/body.
  action = '',
  actorPhoto = '',
  matchDate = '',
  placeName = '',
}) => {
  // Keep the sender in the recipients: the server still writes their history
  // record (so the organizer has a full log) and only skips pushing to them.
  const recipients = [...new Set((recipientUserIds ?? []).filter(Boolean))];
  if (!recipients.length) return null;
  return addDataToTable({
    tableName: 'matchNotifications',
    data: {
      isNew: true,
      shiftId: shiftId ?? '',
      senderUserId: senderUserId ?? '',
      recipientUserIds: recipients,
      type,
      title,
      body,
      url,
      from,
      startDate,
      archive,
      pushSender,
      action,
      actorPhoto,
      matchDate,
      placeName,
    },
  });
};

// `title` is passed in already-localized by the caller (this module has no
// react-intl context); falls back to Spanish if omitted.
export const notifyWaitlist = async (waitlist, shift, title, action = '') => {
  if (!waitlist?.length) return;
  const notifTitle = title || `Plaza libre en ${shift.placeName}`;
  // Match events are informational: they go to the `infoNotifications` queue,
  // which the server delivers as a push and logs straight into the recipient's
  // notification history — they never sit in the pending inbox.
  const startDate = shift.startDate?.toDate
    ? getDateFromTimestamp(shift.startDate)
    : shift.startDate ?? '';
  await Promise.all(
    waitlist
      .filter(e => e.messageToken)
      .map(e =>
        addDataToTable({
          tableName: 'infoNotifications',
          data: {
            from: shift.organizerName ?? '',
            to: e.nickname ?? e.userName ?? '',
            userId: e.userId ?? null,
            messageToken: e.messageToken,
            type: 'matchEvents',
            title: notifTitle,
            placeName: shift.placeName ?? '',
            startDate,
            matchDate: startDate,
            action,
            url: `https://www.gamebooking.top/shifts?${shift.id}`,
            isNew: true,
          },
        }),
      ),
  );
};
